// Livestock: cows, bulls and bison roaming the islands (Godot scripts/animals/*.gd).
// Not enemies: robots ignore them, they don't count toward clearing an island, they never
// block a shot meant for a robot unless they're actually in the way. Server-only brains;
// clients see them through the run block of the snapshot (sim/runwire.ts).
//
//   COW    placid grazer. Gunfire nearby spooks the herd and it bolts; it calms down 20 s
//          after the last shot. Shoot one and G.U.L.P. bills you for it.
//   BULL   one per island. Ignores you until provoked (shot, or a cow of its herd shot
//          near it), then charges and gores: one landed gore per provocation, then it
//          breaks off (Godot 2026-07-03 rule). Fights to the death if you keep at it.
//   BISON  a heavier, shaggier bull that gives up the chase if it loses sight of you.

import { DT, Rng, clamp, hash32, len2, secs, wrapAngle } from './core';
import { PlayerState, isTargetable } from './player';
import { NO_GROUND, World } from './world';

export const enum CType { Cow = 0, Bull = 1, Bison = 2 }
export const enum CState { Graze = 0, Walk = 1, Flee = 2, Charge = 3, Gore = 4, Dead = 5 }

export interface CritterDef { name: string; hp: number; radius: number; height: number; walk: number; flee: number; charge: number; aggro: boolean; gore: number; knock: number; giveUpLos: boolean }
export const CRITTERS: CritterDef[] = [
  { name: 'Cow', hp: 200, radius: 0.9, height: 1.5, walk: 1.5, flee: 8.5, charge: 0, aggro: false, gore: 0, knock: 0, giveUpLos: false },
  { name: 'Bull', hp: 260, radius: 1.0, height: 1.7, walk: 1.7, flee: 8.0, charge: 9.5, aggro: true, gore: 26, knock: 26, giveUpLos: false },
  { name: 'Bison', hp: 320, radius: 1.15, height: 1.9, walk: 1.4, flee: 7.5, charge: 8.6, aggro: true, gore: 22, knock: 22, giveUpLos: true },
];

export interface Critter {
  id: number;
  type: CType;
  x: number; y: number; z: number; yaw: number;
  vx: number; vz: number;
  hp: number; maxHp: number;
  st: CState; stT: number;
  homeX: number; homeZ: number;
  tx: number; tz: number;
  spookT: number;
  fromX: number; fromZ: number;
  target: number;
  losT: number;
  deadT: number;
}

export const MAX_CRITTERS = 12;
const SPOOK = secs(20);
const HEAR_R = 34;
const ALERT_R = 22;

/** Who lives on this island: a herd of cows plus a bull (or bison on later islands). */
export function spawnCritters(world: World, island: number): Critter[] {
  const rng = new Rng(hash32(world.seed, 0xc0b5));
  const out: Critter[] = [];
  let id = 1;
  const main = world.islands[0];
  // herds graze in the meadow: off the arena floor, off the town street, away from spawn
  const herds = island % 3 === 2 ? 1 : 2;
  for (let h = 0; h < herds; h++) {
    let hx = 0, hz = 0;
    for (let tries = 0; tries < 60; tries++) {
      const a = rng.range(0, Math.PI * 2), r = rng.range(38, main.radius * 0.7);
      hx = Math.cos(a) * r; hz = Math.sin(a) * r;
      if (world.terrain(hx, hz) === NO_GROUND || world.townMask(hx, hz) > 0.01) continue;
      if (len2(hx - world.playerSpawn.x, hz - world.playerSpawn.z) < 20) continue;
      if (len2(hx - world.gate.x, hz - world.gate.z) < 20) continue;
      if (len2(hx - world.mesa.x, hz - world.mesa.z) < world.mesa.r + 4) continue;
      break;
    }
    const cows = island % 3 === 2 ? 3 : rng.int(3, 4);
    for (let i = 0; i < cows; i++) out.push(make(id++, CType.Cow, hx + rng.range(-5, 5), hz + rng.range(-5, 5), world, rng));
    if (h === 0 && island % 3 !== 2) {
      // one bull per island; on the second island of a village the herd is guarded by bison
      if (island % 3 === 1) { out.push(make(id++, CType.Bison, hx + rng.range(-6, 6), hz + rng.range(-6, 6), world, rng)); out.push(make(id++, CType.Bison, hx + rng.range(-6, 6), hz + rng.range(-6, 6), world, rng)); }
      else out.push(make(id++, CType.Bull, hx + rng.range(-6, 6), hz + rng.range(-6, 6), world, rng));
    }
  }
  return out.slice(0, MAX_CRITTERS);
}

function make(id: number, type: CType, x: number, z: number, world: World, rng: Rng): Critter {
  const d = CRITTERS[type];
  let y = world.terrain(x, z);
  if (y === NO_GROUND) { x *= 0.8; z *= 0.8; y = world.terrain(x, z); if (y === NO_GROUND) y = 0; }
  return {
    id, type, x, y, z, yaw: rng.range(-Math.PI, Math.PI), vx: 0, vz: 0, hp: d.hp, maxHp: d.hp,
    st: CState.Graze, stT: secs(rng.range(1, 6)), homeX: x, homeZ: z, tx: x, tz: z,
    spookT: 0, fromX: 0, fromZ: 0, target: -1, losT: 0, deadT: 0,
  };
}

/** A gunshot or blast at (x,z): everything in earshot bolts away from it. */
export function critterNoise(list: Critter[], x: number, z: number, loud = 1) {
  const r = HEAR_R * loud;
  for (const c of list) {
    if (c.st === CState.Dead || c.st === CState.Charge || c.st === CState.Gore) continue;
    if (len2(c.x - x, c.z - z) > r) continue;
    c.spookT = SPOOK;
    c.fromX = x; c.fromZ = z;
    if (c.st !== CState.Flee) { c.st = CState.Flee; c.stT = secs(2.5); }
  }
}

/** Damage a critter. Returns true if it died. Bulls and bison take it personally. */
export function hurtCritter(list: Critter[], c: Critter, dmg: number, by: number, fromX: number, fromZ: number): boolean {
  if (c.st === CState.Dead) return false;
  c.hp -= dmg;
  if (c.hp <= 0) { c.hp = 0; c.st = CState.Dead; c.deadT = secs(10); c.vx = c.vz = 0; return true; }
  const d = CRITTERS[c.type];
  if (d.aggro && by >= 0) provoke(c, by);
  else { c.spookT = SPOOK; c.fromX = fromX; c.fromZ = fromZ; c.st = CState.Flee; c.stT = secs(3); }
  // a hurt cow rallies the herd's bull
  if (!d.aggro && by >= 0) {
    for (const o of list) if (o !== c && CRITTERS[o.type].aggro && o.st !== CState.Dead && len2(o.x - c.x, o.z - c.z) < ALERT_R) provoke(o, by);
  }
  return false;
}

function provoke(c: Critter, by: number) {
  if (c.st === CState.Charge && c.target === by) return;
  c.target = by; c.st = CState.Charge; c.stT = secs(12); c.losT = 0;
}

export interface CritterHost {
  world: World;
  players: Map<number, PlayerState>;
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
}

export function stepCritters(list: Critter[], h: CritterHost, rng: Rng) {
  for (const c of list) {
    const d = CRITTERS[c.type];
    if (c.st === CState.Dead) { if (c.deadT > 0) c.deadT--; continue; }
    if (c.spookT > 0) c.spookT--;
    c.stT--;
    let wantX = 0, wantZ = 0, speed = 0, turn = 3;
    switch (c.st) {
      case CState.Graze:
        if (c.stT <= 0) {
          // amble to a fresh spot near home
          const a = rng.range(0, Math.PI * 2), r = rng.range(2, 12);
          c.tx = c.homeX + Math.cos(a) * r; c.tz = c.homeZ + Math.sin(a) * r;
          c.st = CState.Walk; c.stT = secs(10);
        }
        break;
      case CState.Walk: {
        const dx = c.tx - c.x, dz = c.tz - c.z, l = len2(dx, dz);
        if (l < 0.8 || c.stT <= 0) { c.st = CState.Graze; c.stT = secs(rng.range(4, 10)); break; }
        wantX = dx / l; wantZ = dz / l; speed = d.walk; turn = 1.6;
        break;
      }
      case CState.Flee: {
        let dx = c.x - c.fromX, dz = c.z - c.fromZ;
        const l = len2(dx, dz) || 1;
        dx /= l; dz /= l;
        wantX = dx; wantZ = dz; speed = d.flee * (c.stT > 0 ? 1 : 0.45);
        turn = 4;
        // run out of steam far from the noise; graze nervously where you stopped
        if (c.spookT <= 0 || (c.stT <= 0 && l > HEAR_R)) { c.st = CState.Graze; c.stT = secs(rng.range(2, 5)); c.homeX = c.x; c.homeZ = c.z; }
        break;
      }
      case CState.Charge: {
        const p = c.target >= 0 ? h.players.get(c.target) : undefined;
        if (!p || !isTargetable(p) || p.phase !== 0 || c.stT <= 0) { calm(c); break; }
        const dx = p.x - c.x, dz = p.z - c.z, l = len2(dx, dz) || 1;
        if (d.giveUpLos) {
          if (!h.world.lineOfSight(c.x, c.y + 1.2, c.z, p.x, p.y + 1, p.z)) { if (++c.losT > secs(3)) { calm(c); break; } } else c.losT = 0;
        }
        wantX = dx / l; wantZ = dz / l; speed = d.charge; turn = 2.6;
        if (l < d.radius + 1.6 && Math.abs(p.y - c.y) < 1.8) {
          // gore: one landed hit per provocation, then it trots off
          const fx = -Math.sin(c.yaw), fz = -Math.cos(c.yaw);
          h.hurtPlayer(p, d.gore, c.x, c.z, 4, fx * d.knock, fz * d.knock);
          c.st = CState.Gore; c.stT = secs(1.2);
        }
        break;
      }
      case CState.Gore:
        speed = 0;
        if (c.stT <= 0) calm(c);
        break;
    }
    // steer: turn toward the wanted heading, walk forward; respect the cliff edge
    if (speed > 0) {
      const want = Math.atan2(-wantX, -wantZ);
      c.yaw = wrapAngle(c.yaw + clamp(wrapAngle(want - c.yaw), -turn * DT, turn * DT));
    }
    const fx = -Math.sin(c.yaw), fz = -Math.cos(c.yaw);
    c.vx += (fx * speed - c.vx) * 0.12; c.vz += (fz * speed - c.vz) * 0.12;
    const nx = c.x + c.vx * DT, nz = c.z + c.vz * DT;
    // look a body length ahead: no ledges, no walls, no walking off the island
    const ax = nx + fx * 2.5, az = nz + fz * 2.5;
    const ga = h.world.terrain(ax, az), g = h.world.terrain(nx, nz);
    if (g === NO_GROUND || ga === NO_GROUND || ga < c.y - 2.2 || g > c.y + 0.9) {
      c.vx = c.vz = 0;
      c.yaw = wrapAngle(c.yaw + Math.PI * 0.6);
      if (c.st === CState.Walk) { c.st = CState.Graze; c.stT = secs(2); }
      continue;
    }
    const pos = { x: nx, y: g, z: nz };
    h.world.pushOut(pos, d.radius * 0.8, d.height);
    c.x = pos.x; c.z = pos.z; c.y = h.world.terrain(c.x, c.z);
    if (c.y === NO_GROUND) c.y = g;
  }
}

function calm(c: Critter) {
  c.target = -1; c.st = CState.Graze; c.stT = secs(3); c.homeX = c.x; c.homeZ = c.z; c.spookT = 0;
}

/** Ray vs critter bodies (a sphere at mid-body). Returns index or -1. */
export function critterRaycast(list: Critter[], ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, out: { t: number }) {
  let best = -1;
  out.t = maxT;
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    if (c.st === CState.Dead) continue;
    const d = CRITTERS[c.type];
    // two spheres along the body: shoulders and haunch
    const fx = -Math.sin(c.yaw), fz = -Math.cos(c.yaw);
    for (const k of [0.55, -0.55]) {
      const cx = c.x + fx * k, cz = c.z + fz * k, cy = c.y + d.height * 0.62;
      const lx = cx - ox, ly = cy - oy, lz = cz - oz;
      const tca = lx * dx + ly * dy + lz * dz;
      const d2 = lx * lx + ly * ly + lz * lz - tca * tca;
      const r = d.radius * 0.85;
      if (d2 > r * r) continue;
      const t = tca - Math.sqrt(r * r - d2);
      if (t >= 0 && t < out.t) { out.t = t; best = i; }
    }
  }
  return best;
}
